// ═══════════════════════════════════════════════════════════
// PSP Suite Bar — OpenGov Platform Service Platform chrome
// Row 1 (56px): hamburger · wordmark + entity · spacer ·
//               OG Assist · chat · notifications · avatar
// + PSPMenu (left overlay panel)
// + PSPAssistPanel (right push panel, 380px)
// ═══════════════════════════════════════════════════════════

import { useState, useRef, useEffect } from 'react';
import {
  IconMenu2, IconChevronDown,
  IconBell, IconX, IconArrowUp, IconHistory,
  IconHome, IconAlertOctagon, IconChartBar, IconCash,
  IconFileText, IconShoppingCart, IconTool, IconReceipt,
  IconRobot, IconDatabase, IconChartPie, IconSettings, IconLogout,
} from '@tabler/icons-react';
import { callClaude } from '../../lib/api-switch';
import { useAuthStore } from '../../store/authStore';

// ── Suite product list ────────────────────────────────────────
const SUITE_PRODUCTS = [
  { key: 'emc',         label: 'Emergency Management Cloud', icon: <IconAlertOctagon size={18} />, active: true  },
  { key: 'budgeting',   label: 'Budgeting & Planning',       icon: <IconChartBar     size={18} />, active: false },
  { key: 'financials',  label: 'Financials',                 icon: <IconCash         size={18} />, active: false },
  { key: 'permitting',  label: 'Permitting & Licensing',     icon: <IconFileText     size={18} />, active: false },
  { key: 'procurement', label: 'Procurement',                icon: <IconShoppingCart size={18} />, active: false },
  { key: 'eam',         label: 'Asset Management',           icon: <IconTool         size={18} />, active: false },
  { key: 'tax-revenue', label: 'Tax & Revenue',              icon: <IconReceipt      size={18} />, active: false },
];
const SUITE_CAPS = [
  { key: 'agent-studio', label: 'Agent Studio', icon: <IconRobot     size={18} /> },
  { key: 'data-manager', label: 'Data Manager', icon: <IconDatabase  size={18} /> },
  { key: 'reporting',    label: 'Reporting',    icon: <IconChartPie  size={18} /> },
];

// ── OG Assist suggestions ──────────────────────────────────────
const ASSIST_SUGGESTIONS = [
  'Summarize the active incident in one paragraph.',
  'What needs my attention right now across modules?',
  'Draft a shift-change handoff briefing.',
  'Generate a 0600 SitRep — review before sending.',
];

// ── Shared icon-ring button style helper ──────────────────────
function iconRingStyle(): React.CSSProperties {
  return {
    width: 34, height: 34, borderRadius: '50%',
    border: '1px solid var(--foundations-colors-blurple-700)',
    background: '#fff', cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  };
}

// ── PSP Suite Bar ─────────────────────────────────────────────
interface PSPSuiteBarProps {
  entity?: string;
  userInitials?: string;
  userName?: string;
  userEmail?: string;
  notifBadge?: number;
  assistOpen?: boolean;
  navSlot?: React.ReactNode;
  trailingSlot?: React.ReactNode;
  onMenu?: () => void;
  onAssist?: () => void;
  onAdmin?: () => void;
  onSignOut?: () => void;
}

export function PSPSuiteBar({
  entity = 'Emergency Management Console',
  userInitials = '??',
  userName = '',
  userEmail = '',
  notifBadge = 0,
  assistOpen = false,
  navSlot,
  trailingSlot,
  onMenu,
  onAssist,
  onAdmin,
  onSignOut,
}: PSPSuiteBarProps) {
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);
  return (
    <div
      role="banner"
      aria-label="OpenGov Suite"
      style={{
        height: 56, flexShrink: 0,
        background: '#fff',
        borderBottom: '1px solid var(--foundations-colors-gray-200)',
        display: 'flex', alignItems: 'center', padding: '0 16px', gap: 8,
        position: 'relative', zIndex: 1021,
      }}
    >
      {/* Hamburger */}
      <button
        onClick={onMenu}
        aria-label="OpenGov suite menu"
        style={{
          width: 36, height: 36, borderRadius: '50%', border: 'none',
          background: 'transparent', cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <IconMenu2 size={22} color="var(--foundations-colors-gray-1000)" />
      </button>

      {/* Wordmark + caret + entity */}
      <button
        onClick={onMenu}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          height: 36, padding: '0 8px', marginLeft: 2,
          background: 'transparent', border: 'none', cursor: 'pointer',
          fontFamily: 'inherit', textAlign: 'left', flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <img
              src="/cds/assets/logo/OpenGov-Logo-Blurple.svg"
              alt="OpenGov"
              style={{ height: 18, display: 'block' }}
            />
            <IconChevronDown size={16} color="var(--foundations-colors-blurple-700)" />
          </span>
          <span style={{ fontSize: 12, color: 'var(--foundations-colors-gray-700)', marginTop: 2 }}>
            {entity}
          </span>
        </div>
      </button>

      {navSlot}

      <div style={{ flex: 1 }} />

      {trailingSlot}

      {/* OG Assist pill */}
      <button
        onClick={onAssist}
        aria-pressed={assistOpen}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          height: 32, padding: '0 16px', borderRadius: 4,
          border: '1px solid var(--foundations-colors-blurple-700)',
          background: assistOpen ? 'var(--foundations-colors-blurple-50)' : '#fff',
          color: 'var(--foundations-colors-blurple-700)',
          fontSize: 14, fontWeight: 600,
          cursor: 'pointer', fontFamily: 'inherit', letterSpacing: 0.2,
          flexShrink: 0,
        }}
      >
        <img src="/cds/assets/logo/OpenGov-Wand-Blurple.svg" alt="" style={{ width: 14, height: 14 }} />
        OG Assist
      </button>


      {/* Avatar + account dropdown */}
      <div ref={accountRef} style={{ position: 'relative', flexShrink: 0 }}>
        <button
          onClick={() => setAccountOpen(o => !o)}
          aria-label={`Account: ${userName}`}
          aria-expanded={accountOpen}
          title={userName}
          style={{
            width: 34, height: 34, borderRadius: '50%',
            background: '#0B7C66', color: '#fff',
            fontSize: 12, fontWeight: 700, letterSpacing: 0.5,
            border: 'none', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {userInitials}
        </button>

        {accountOpen && (
          <div style={{
            position: 'absolute', top: 42, right: 0, zIndex: 2000,
            background: '#fff', borderRadius: 4,
            border: '1px solid var(--foundations-colors-gray-200)',
            boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
            minWidth: 220, overflow: 'hidden',
          }}>
            {/* Identity */}
            <div style={{ padding: '16px 14px 8px', borderBottom: '1px solid var(--foundations-colors-gray-200)' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--foundations-colors-gray-1000)' }}>{userName}</div>
              {userEmail && <div style={{ fontSize: 12, color: 'var(--foundations-colors-gray-600)', marginTop: 2 }}>{userEmail}</div>}
            </div>
            {/* Actions */}
            <button
              onClick={() => { setAccountOpen(false); onAdmin?.(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                width: '100%', padding: '8px 16px', border: 'none',
                background: 'transparent', cursor: 'pointer', textAlign: 'left',
                fontSize: 14, color: 'var(--foundations-colors-gray-900)',
                fontFamily: 'inherit',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--foundations-colors-gray-50)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <IconSettings size={15} color="var(--foundations-colors-gray-700)" />
              Administration
            </button>
            <button
              onClick={() => { setAccountOpen(false); onSignOut?.(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                width: '100%', padding: '8px 16px', border: 'none',
                background: 'transparent', cursor: 'pointer', textAlign: 'left',
                fontSize: 14, color: 'var(--foundations-colors-red-600)',
                fontFamily: 'inherit',
                borderTop: '1px solid var(--foundations-colors-gray-200)',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--foundations-colors-red-50)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <IconLogout size={15} />
              Sign Out
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Suite Menu (left overlay) ─────────────────────────────────
interface PSPMenuProps {
  open: boolean;
  entity?: string;
  onClose?: () => void;
}

export function PSPMenu({ open, entity = 'Emergency Management Console', onClose }: PSPMenuProps) {
  if (!open) return null;
  return (
    <>
      {/* Scrim */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.18)',
          zIndex: 1040, animation: 'emc-fade 200ms ease-out',
        }}
      />
      <aside
        role="dialog"
        aria-label="OpenGov suite menu"
        style={{
          position: 'fixed', top: 0, bottom: 0, left: 0, width: 280,
          background: '#fff', zIndex: 1041,
          boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
          display: 'flex', flexDirection: 'column',
          animation: 'emc-slide-left 220ms cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        {/* Header row */}
        <div style={{
          height: 56, padding: '0 12px 0 16px', display: 'flex', alignItems: 'center', gap: 8,
          borderBottom: '1px solid var(--foundations-colors-gray-200)',
          flexShrink: 0,
        }}>
          <button
            onClick={onClose}
            aria-label="Close menu"
            style={{
              width: 36, height: 36, borderRadius: '50%', border: 'none',
              background: 'transparent', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <IconMenu2 size={22} />
          </button>
          <img src="/cds/assets/logo/OpenGov-Logo-Blurple.svg" alt="OpenGov" style={{ height: 18 }} />
        </div>

        {/* Entity selector */}
        <div style={{ padding: '16px 16px 8px', flexShrink: 0 }}>
          <div style={{ fontSize: 12, color: 'var(--foundations-colors-gray-700)', marginBottom: 8 }}>Entity</div>
          <div style={{
            height: 36, border: '1px solid var(--foundations-colors-gray-300)', borderRadius: 4,
            display: 'flex', alignItems: 'center', padding: '0 8px',
            fontSize: 14, color: 'var(--foundations-colors-gray-1000)', gap: 8,
          }}>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {entity}
            </span>
            <IconChevronDown size={18} color="var(--foundations-colors-gray-700)" />
          </div>
        </div>

        {/* Nav list */}
        <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 16 }}>
          <SuiteSection label="Your Action Hub">
            <SuiteRow icon={<IconHome size={18} />} label="Command Center" />
          </SuiteSection>
          <SuiteSection label="Products">
            {SUITE_PRODUCTS.map(p => <SuiteRow key={p.key} icon={p.icon} label={p.label} active={p.active} />)}
          </SuiteSection>
          <SuiteSection label="Capabilities">
            {SUITE_CAPS.map(p => <SuiteRow key={p.key} icon={p.icon} label={p.label} />)}
          </SuiteSection>
        </div>

        <div style={{
          flexShrink: 0, borderTop: '1px solid var(--foundations-colors-gray-200)', padding: '8px 0',
        }}>
          <SuiteRow icon={<IconSettings size={18} />} label="Settings" />
        </div>
      </aside>
    </>
  );
}

function SuiteSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ padding: '8px 0' }}>
      <div style={{
        padding: '8px 16px', fontSize: 12, fontWeight: 700,
        letterSpacing: 0.6, textTransform: 'uppercase',
        color: 'var(--foundations-colors-gray-700)',
      }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function SuiteRow({
  icon, label, active = false,
}: { icon: React.ReactNode; label: string; active?: boolean }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: '100%', height: 36, border: 'none',
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '0 16px 0 28px', textAlign: 'left', cursor: 'pointer',
        background: active
          ? 'var(--foundations-colors-blurple-50)'
          : hovered ? 'var(--foundations-colors-gray-50)' : 'transparent',
        color: active ? 'var(--foundations-colors-blurple-700)' : 'var(--foundations-colors-gray-1000)',
        fontWeight: active ? 600 : 400, fontSize: 14, fontFamily: 'inherit',
      }}
    >
      <span style={{ color: active ? 'var(--foundations-colors-blurple-700)' : 'var(--foundations-colors-gray-700)', display: 'flex' }}>
        {icon}
      </span>
      <span style={{ flex: 1 }}>{label}</span>
    </button>
  );
}

// ── OG Assist push panel ──────────────────────────────────────
interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface PSPAssistPanelProps {
  open: boolean;
  width?: number;
  onClose?: () => void;
}

export function PSPAssistPanel({ open, width = 380, onClose }: PSPAssistPanelProps) {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeIncident = useAuthStore(s => s.activeIncident);

  useEffect(() => {
    if (open) textareaRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  async function handleSend() {
    const text = message.trim();
    if (!text || loading) return;

    const next: ChatMessage[] = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setMessage('');
    setLoading(true);

    try {
      const system = `You are OG Assist, an AI embedded in OpenGov's Emergency Management Console (EMC). You help EOC staff and incident commanders manage emergency operations.${activeIncident ? ` The active incident is: "${activeIncident.name}".` : ''} Be concise and actionable. Use plain text — no markdown headers.`;
      const reply = await callClaude(
        next.map(m => ({ role: m.role, content: m.content })),
        { system, maxTokens: 1024 },
      );
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Something went wrong. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  if (!open) return null;

  return (
    <aside
      aria-label="OG Assist"
      style={{
        width, flexShrink: 0,
        background: '#fff',
        borderLeft: '1px solid var(--foundations-colors-gray-200)',
        display: 'flex', flexDirection: 'column',
        animation: 'emc-slide-right 240ms cubic-bezier(0.4,0,0.2,1)',
        height: '100%',
      }}
    >
      {/* Header */}
      <div style={{
        height: 48, flexShrink: 0,
        borderBottom: '1px solid var(--foundations-colors-gray-200)',
        display: 'flex', alignItems: 'center', padding: '0 8px 0 16px', gap: 8,
      }}>
        <img src="/cds/assets/logo/OpenGov-Wand-Blurple.svg" alt="" style={{ width: 16, height: 16 }} />
        <div style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>Agent Chat</div>
        {messages.length > 0 && (
          <button
            aria-label="Clear chat"
            onClick={() => setMessages([])}
            style={iconBtn34()}
            title="Clear conversation"
          >
            <IconHistory size={18} color="var(--foundations-colors-gray-700)" />
          </button>
        )}
        <button onClick={onClose} aria-label="Close OG Assist" style={iconBtn34()}>
          <IconX size={18} color="var(--foundations-colors-gray-700)" />
        </button>
      </div>

      {/* Body: welcome or conversation */}
      <div
        ref={scrollRef}
        style={{
          flex: 1, overflowY: 'auto', padding: messages.length === 0 ? '32px 18px 24px' : '16px 16px 8px',
          display: 'flex', flexDirection: 'column',
          alignItems: messages.length === 0 ? 'center' : 'stretch',
          gap: messages.length === 0 ? 14 : 12,
        }}
      >
        {messages.length === 0 ? (
          <>
            <img src="/cds/assets/logo/OpenGov-Wand-Blurple.svg" alt="" style={{ width: 28, height: 28, marginBottom: 4 }} />
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--foundations-colors-gray-1000)' }}>
              Welcome to OG Assist
            </div>
            <div style={{
              fontSize: 12.5, color: 'var(--foundations-colors-gray-700)',
              textAlign: 'center', lineHeight: 1.5, maxWidth: 280,
            }}>
              Scoped to your active incident. Ask anything across EMC modules.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', marginTop: 8 }}>
              {ASSIST_SUGGESTIONS.map((s, i) => (
                <SuggestionBtn key={i} text={s} onClick={() => setMessage(s)} />
              ))}
            </div>
          </>
        ) : (
          <>
            {messages.map((m, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  flexDirection: m.role === 'user' ? 'row-reverse' : 'row',
                  gap: 8, alignItems: 'flex-start',
                }}
              >
                {m.role === 'assistant' && (
                  <img src="/cds/assets/logo/OpenGov-Wand-Blurple.svg" alt="" style={{ width: 18, height: 18, marginTop: 3, flexShrink: 0 }} />
                )}
                <div style={{
                  maxWidth: '80%',
                  padding: '8px 16px',
                  borderRadius: m.role === 'user' ? '12px 12px 2px 16px' : '12px 12px 12px 2px',
                  background: m.role === 'user' ? 'var(--foundations-colors-blurple-700)' : 'var(--foundations-colors-gray-50)',
                  color: m.role === 'user' ? '#fff' : 'var(--foundations-colors-gray-1000)',
                  fontSize: 14, lineHeight: 1.5,
                  border: m.role === 'assistant' ? '1px solid var(--foundations-colors-gray-200)' : 'none',
                  whiteSpace: 'pre-wrap',
                }}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <img src="/cds/assets/logo/OpenGov-Wand-Blurple.svg" alt="" style={{ width: 18, height: 18, flexShrink: 0 }} />
                <div style={{
                  padding: '8px 16px', borderRadius: '4px 4px 4px 4px',
                  background: 'var(--foundations-colors-gray-50)',
                  border: '1px solid var(--foundations-colors-gray-200)',
                  fontSize: 14, color: 'var(--foundations-colors-gray-500)',
                }}>
                  Thinking…
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Input */}
      <div style={{ padding: 16, borderTop: '1px solid var(--foundations-colors-gray-200)', flexShrink: 0 }}>
        <div style={{
          border: '1px solid var(--foundations-colors-gray-300)', borderRadius: 4,
          padding: '8px 10px 10px 16px', display: 'flex', alignItems: 'flex-end', gap: 8, minHeight: 64,
        }}>
          <textarea
            ref={textareaRef}
            value={message}
            onChange={e => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your message…"
            rows={1}
            style={{
              flex: 1, border: 'none', outline: 'none', resize: 'none',
              fontFamily: 'inherit', fontSize: 14,
              color: 'var(--foundations-colors-gray-1000)',
              background: 'transparent',
            }}
          />
          <button
            aria-label="Send"
            onClick={handleSend}
            disabled={!message.trim() || loading}
            style={{
              width: 28, height: 28, borderRadius: '50%', border: 'none',
              background: message.trim() && !loading ? 'var(--foundations-colors-blurple-700)' : 'var(--foundations-colors-gray-300)',
              color: '#fff',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              cursor: message.trim() && !loading ? 'pointer' : 'default',
              flexShrink: 0,
            }}
          >
            <IconArrowUp size={16} />
          </button>
        </div>
        <div style={{
          marginTop: 8, fontSize: 12, color: 'var(--foundations-colors-gray-700)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <img src="/cds/assets/logo/OpenGov-Wand-Gray.svg" alt="" style={{ width: 12, height: 12 }} />
          AI can make mistakes. Please carefully check all outputs.
        </div>
      </div>
    </aside>
  );
}

function SuggestionBtn({ text, onClick }: { text: string; onClick: () => void }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '8px 16px', textAlign: 'left',
        borderRadius: 4, border: '1px solid var(--foundations-colors-gray-200)',
        background: hovered ? 'var(--foundations-colors-gray-50)' : '#fff',
        fontSize: 12.5, color: 'var(--foundations-colors-gray-1000)',
        cursor: 'pointer', fontFamily: 'inherit', lineHeight: 1.4,
      }}
    >
      {text}
    </button>
  );
}

function iconBtn34(): React.CSSProperties {
  return {
    width: 34, height: 34, borderRadius: '50%', border: 'none',
    background: 'transparent', cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  };
}
